import type { Libraries } from "@react-google-maps/api";
import { GoogleMap, Marker } from "@react-google-maps/api";
import React, { useEffect, useRef, useState } from "react";

import { QUERY_RADIUS, getBoundsRadius } from "../../api/locations";
import {
    LatLng,
    Location,
    LocationID,
    LocationProximity,
    getDealerIndices,
    isShowroom,
    isTechnicurean,
    isoLatitude,
    isoLocationID,
    isoLongitude,
    toGoogleLatLng,
} from "../../models";
import { mapPinD } from "../Svg";
import { getGlyph } from "./IndexGlyph";
import { SearchAreaButton } from "./SearchAreaButton";
import { showroomMarkerIcon } from "./ShowroomGlyph";

import colorVariables from "../../styles/colors-2026.module.scss";
import styles from "./LocationListItem.module.scss";

export const GOOGLE_LIBRARIES: Libraries = ["places", "geometry"];

const containerStyle = {
    width: "100%",
    height: "100%",
};

const getPinColor = (location: Location, focused: boolean): string => {
    if (isTechnicurean(location)) {
        return colorVariables.red;
    }
    return focused ? colorVariables.black : colorVariables.gray500;
};

export const MarkerMap = (props: {
    locations: LocationProximity[];
    focusedLocationID: LocationID | null;
    setFocusedLocationID: (rlid: LocationID | null) => void;
    center: LatLng;
    bounds?: google.maps.LatLngBounds;
    onSearchArea?: (point: LatLng, bounds: google.maps.LatLngBounds) => void;
}) => {
    const mapRef = useRef<google.maps.Map | null>(null);
    const focusedMarkerRefs = useRef(new Map<LocationID, google.maps.Marker>());

    const [mapReady, setMapReady] = useState(false);
    const dealerIndices = getDealerIndices(props.locations);

    const focusedLocation =
        props.locations.find((locProx) => {
            return locProx.location.id === props.focusedLocationID;
        }) ?? null;

    useEffect(() => {
        if (!mapRef.current || !mapReady || !focusedLocation) return;
        mapRef.current.panTo(toGoogleLatLng(focusedLocation.location.location));
    }, [mapReady, props.focusedLocationID, props.locations]);

    useEffect(() => {
        if (!mapRef.current || !mapReady || props.bounds) return;
        mapRef.current.panTo(toGoogleLatLng(props.center));
    }, [mapReady, props.center]);

    useEffect(() => {
        if (!mapRef.current || !mapReady || !props.bounds) return;
        mapRef.current.panTo(props.bounds.getCenter());
        const radius = getBoundsRadius(props.bounds);
        if (radius > QUERY_RADIUS) {
            mapRef.current.fitBounds(props.bounds);
        } else {
            mapRef.current.setZoom(10);
        }
    }, [mapReady, props.bounds]);

    const handleSearchArea = () => {
        const map = mapRef.current;
        if (!map || !props.onSearchArea) return;
        const center = map.getCenter();
        const bounds = map.getBounds();
        if (!center || !bounds) return;
        props.onSearchArea(
            {
                lat: isoLatitude.wrap(center.lat()),
                lng: isoLongitude.wrap(center.lng()),
            },
            bounds,
        );
    };

    return (
        <>
            {props.onSearchArea && (
                <SearchAreaButton onPress={handleSearchArea} />
            )}
            <GoogleMap
                mapContainerStyle={containerStyle}
                zoom={10}
                options={{
                    disableDefaultUI: true,
                    keyboardShortcuts: true,
                    styles: [
                        {
                            elementType: "geometry",
                            stylers: [{ saturation: -100 }],
                        },
                        {
                            elementType: "labels.text.fill",
                            stylers: [{ color: "#54514e" }],
                        },
                        {
                            elementType: "labels.text.stroke",
                            stylers: [{ color: "#f0edec" }],
                        },
                        {
                            featureType: "road",
                            elementType: "geometry",
                            stylers: [{ color: "#e3e2e0" }],
                        },
                        {
                            featureType: "water",
                            elementType: "geometry.fill",
                            stylers: [{ color: "#c8c5c1" }],
                        },
                        {
                            featureType: "poi",
                            stylers: [{ visibility: "off" }],
                        },
                        {
                            featureType: "transit",
                            stylers: [{ visibility: "off" }],
                        },
                    ],
                }}
                onLoad={(map) => {
                    map.setCenter(toGoogleLatLng(props.center));
                    mapRef.current = map;
                    setMapReady(true);
                }}
            >
                {props.locations.map(({ location }) => {
                    const showroom = isShowroom(location);
                    const di = dealerIndices.get(
                        isoLocationID.unwrap(location.id),
                    );
                    return (
                        <Marker
                            key={`${isoLocationID.unwrap(location.id)}`}
                            label={
                                showroom || di === undefined
                                    ? undefined
                                    : {
                                          text: getGlyph(di),
                                          className: styles.markerGlyph,
                                          color: colorVariables.white,
                                          fontSize: "12px",
                                      }
                            }
                            position={toGoogleLatLng(location.location)}
                            onLoad={(marker) => {
                                focusedMarkerRefs.current.set(
                                    location.id,
                                    marker,
                                );
                            }}
                            onUnmount={() => {
                                focusedMarkerRefs.current.delete(location.id);
                            }}
                            onClick={() => {
                                props.setFocusedLocationID(location.id);
                            }}
                            icon={
                                showroom
                                    ? {
                                          url: showroomMarkerIcon,
                                          scaledSize: new google.maps.Size(
                                              29,
                                              29,
                                          ),
                                          anchor: new google.maps.Point(
                                              14.5,
                                              14.5,
                                          ),
                                      }
                                    : {
                                          path: mapPinD,
                                          fillColor: getPinColor(
                                              location,
                                              location.id ===
                                                  focusedLocation?.location.id,
                                          ),
                                          fillOpacity: 1,
                                          scale: 1,
                                          strokeWeight: 0,
                                          labelOrigin: new google.maps.Point(
                                              10.8,
                                              12,
                                          ),
                                      }
                            }
                        />
                    );
                })}
            </GoogleMap>
        </>
    );
};
